// Address suggestions for the delivery form, from DAWA — Danmarks Adressers
// Web API (https://api.dataforsyningen.dk).
//
// The official register of every Danish address, published by
// Klimadatastyrelsen. Free, keyless, CORS-open, so the browser calls it
// directly and no key or proxy is involved.
//
// This is the suggestion list only. What the customer picks is handed to
// /woltDelivery/geocode, which stays the single source of the coordinates and
// final values the order is placed with — see utils/geocode.js.
//
// Docs: https://dawadocs.dataforsyningen.dk/dok/api/autocomplete

const DAWA_BASE = 'https://api.dataforsyningen.dk';

// Enough choices to find the address, few enough to scan on a phone.
const SUGGESTION_LIMIT = 8;

// Below this the register returns half of Denmark; not worth a round trip.
export const MIN_QUERY_LENGTH = 2;

/**
 * DAWA only covers Denmark, so suggestions are gated on the venue being Danish.
 * A venue we can't identify as Danish keeps the plain manual address fields,
 * which still resolve through the geocode lookup on blur.
 */
export const isDawaSupported = (restaurant) => {
  const currency = restaurant?.currency?.code ?? restaurant?.Currency?.code ?? restaurant?.Currency;

  return String(currency || '').toUpperCase() === 'DKK';
};

/**
 * Typeahead over the register. DAWA stages the search itself: a partial street
 * returns `vejnavn` suggestions, and once a street is settled it returns full
 * `adgangsadresse` matches. Only the latter carry an address — a `vejnavn`
 * suggestion just narrows the search, so the caller puts its `text` back in the
 * input and searches again from `caretpos`.
 *
 * `fuzzy` is what lets "Rantemestervej 8" still find Rentemestervej 8.
 */
export const fetchAddressSuggestions = async (query, { caretpos, signal } = {}) => {
  const q = (query || '').trim();
  if (q.length < MIN_QUERY_LENGTH) return [];

  const params = new URLSearchParams({
    q,
    type: 'adgangsadresse',
    per_side: SUGGESTION_LIMIT,
    fuzzy: '',
    ...(Number.isInteger(caretpos) ? { caretpos } : {}),
  });

  const response = await fetch(`${DAWA_BASE}/autocomplete?${params}`, { signal });

  if (!response.ok) throw new Error(`DAWA responded ${response.status}`);

  const results = await response.json();
  if (!Array.isArray(results)) return [];

  return results.map((item, index) => ({
    key: item?.data?.id || `${item?.type}-${item?.tekst}-${index}`,
    label: item?.forslagstekst || item?.tekst || '',
    // what goes back into the input when this suggestion is chosen
    text: item?.tekst || '',
    caretpos: item?.caretpos,
    // The register's own split of the address. Filling the form from it is
    // instant; the geocode lookup that follows confirms it and adds the
    // coordinates.
    address:
      item?.type === 'adgangsadresse' && item?.data?.id
        ? {
            street: [item.data.vejnavn, item.data.husnr].filter(Boolean).join(' '),
            zipcode: item.data.postnr || '',
            city: item.data.postnrnavn || '',
          }
        : null,
  }));
};
